import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "./button";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "@/utils/pdfWorker";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { getCertificateFileUrl, isPdfFile } from "@/utils/certificateFile";

const CertificateMediaViewer = ({ file, title = "Certificate" }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [loadError, setLoadError] = useState(false);

  const url = getCertificateFileUrl(file);
  const pdf = isPdfFile(file);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: next }) => {
    setNumPages(next);
    setPageNumber(1);
    setLoadError(false);
  }, []);

  if (!url) {
    return (
      <div className="aspect-video rounded-xl bg-muted flex items-center justify-center">
        <FileText className="w-12 h-12 text-muted-foreground" />
      </div>
    );
  }

  if (!pdf) {
    return (
      <div className="rounded-xl overflow-hidden border bg-muted/30 shadow-sm">
        <img
          src={url}
          alt={title}
          className="w-full h-auto max-h-[70vh] object-contain mx-auto"
        />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-xl border bg-muted/40 p-10 flex flex-col items-center justify-center gap-4 text-center">
        <FileText className="w-14 h-14 text-red-500" />
        <p className="text-muted-foreground">
          Could not render this PDF in the browser.
        </p>
        <Button className="bg-purple-600 hover:bg-purple-700" asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open original PDF
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </Button>
      </div>
    );
  }

  const pageWidth = containerWidth
    ? Math.min(containerWidth - 16, 900) * scale
    : undefined;

  return (
    <div className="space-y-4">
      <div
        ref={containerRef}
        className="rounded-xl border bg-gradient-to-b from-muted/50 to-background overflow-hidden shadow-sm"
      >
        <div className="flex items-center justify-center p-3 sm:p-4 min-h-[320px] overflow-auto">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() => setLoadError(true)}
            loading={
              <div className="w-full max-w-2xl space-y-3 py-8">
                <div className="h-8 w-1/3 mx-auto bg-muted animate-pulse rounded" />
                <div className="aspect-[3/4] w-full bg-muted animate-pulse rounded-lg" />
              </div>
            }
          >
            {pageWidth ? (
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-lg rounded-sm overflow-hidden"
                loading={
                  <div className="aspect-[3/4] w-full max-w-xl bg-muted animate-pulse rounded-lg" />
                }
              />
            ) : (
              <div className="aspect-[3/4] w-full max-w-xl bg-muted animate-pulse rounded-lg" />
            )}
          </Document>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[5.5rem] text-center">
            {numPages ? `${pageNumber} / ${numPages}` : "—"}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!numPages || pageNumber >= numPages}
            onClick={() =>
              setPageNumber((p) => Math.min(numPages || p, p + 1))
            }
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={scale <= 0.75}
            onClick={() => setScale((s) => Math.max(0.75, +(s - 0.15).toFixed(2)))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={scale >= 1.75}
            onClick={() => setScale((s) => Math.min(1.75, +(s + 0.15).toFixed(2)))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              Open PDF
              <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificateMediaViewer;
